/** Reporting periods for vendor numbers (list, orders, analytics) → ISO date bounds (local days). */

export type VendorPeriod = '7d' | '30d' | '90d' | 'month' | 'year' | 'all' | 'custom';
export const VENDOR_PERIODS: Exclude<VendorPeriod, 'custom'>[] = ['7d', '30d', '90d', 'month', 'year', 'all'];

/** Order statuses: analytics count placed → delivered; slips default to the ones still to fulfil. */
export const ORDER_STATUSES = ['pending', 'placed', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'] as const;
export const ANALYTICS_STATUSES = ['placed', 'confirmed', 'processing', 'shipped', 'delivered'];
export const SLIP_STATUSES = ['placed', 'confirmed', 'processing'];

const day = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function periodRange(period: VendorPeriod, custom?: { from?: Date; to?: Date }): { from?: string; to?: string } {
  const now = new Date();
  const back = (days: number) => ({ from: day(new Date(now.getFullYear(), now.getMonth(), now.getDate() - days + 1)), to: day(now) });
  switch (period) {
    case '7d': return back(7);
    case '30d': return back(30);
    case '90d': return back(90);
    case 'month': return { from: day(new Date(now.getFullYear(), now.getMonth(), 1)), to: day(now) };
    case 'year': return { from: day(new Date(now.getFullYear(), 0, 1)), to: day(now) };
    case 'custom': return { from: custom?.from ? day(custom.from) : undefined, to: custom?.to ? day(custom.to) : undefined };
    default: return {};
  }
}

export function formatMoney(amount: number | undefined, currency?: string, locale?: string): string {
  const n = Number(amount) || 0;
  try {
    return new Intl.NumberFormat(locale, currency ? { style: 'currency', currency, maximumFractionDigits: n % 1 ? 2 : 0 } : { maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${currency ? `${currency} ` : ''}${n.toFixed(2)}`;
  }
}

/** Percent change vs the previous period; undefined when there was nothing before. */
export function changePercent(current: number, previous: number): number | undefined {
  if (!previous) return undefined;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}
